import { useSubscription } from "@apollo/client";
import { ChatMessageCreatedSubscription } from "@/entities/contents";
import type { ChatMessageCreatedSubscriptionType } from "@/types/graphql/site.generated";

const STORAGE_KEY = "chatSoundSettings";

export interface ChatSoundSettingsValue {
  reply: boolean;
  mention: boolean;
  join: boolean;
}

const DEFAULT_SETTINGS: ChatSoundSettingsValue = {
  reply: true,
  mention: true,
  join: false,
};

export function getChatSoundSettings(): ChatSoundSettingsValue {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function setChatSoundSettings(settings: ChatSoundSettingsValue): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

// Референс использует статичные .wav-файлы — здесь короткий синтезированный
// тон через Web Audio API (в репозитории нет audio-ассетов, см. Global
// Constraints); частоты условные, но каждое событие отличимо на слух.
const TONE_FREQUENCIES: Record<keyof ChatSoundSettingsValue, number> = {
  join: 440,
  mention: 660,
  reply: 880,
};

function playTone(frequency: number): void {
  if (typeof window === "undefined") return;
  const AudioContextClass =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextClass) return;

  const ctx = new AudioContextClass();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.frequency.value = frequency;
  gain.gain.value = 0.1;
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + 0.15);
  oscillator.onended = () => void ctx.close();
}

export function useChatSoundNotifications(
  chatRoomId: string | null,
  viewerUserId: string | null,
): void {
  // Та же подписка, что у ленты (ChatMessageList), с теми же переменными:
  // Apollo 3 склеивает её в одну по сокету (queryDeduplication), пока обе
  // открываются до первого события, то есть в одном рендере ContentChat.
  // Проверено на @apollo/client 3.14 (спека viewer-db-load, 3). Хук,
  // смонтированный позже ленты, открыл бы вторую подписку на сервере.
  useSubscription<ChatMessageCreatedSubscriptionType>(
    ChatMessageCreatedSubscription,
    {
      variables: { chatRoomId: chatRoomId ?? "" },
      skip: !chatRoomId,
      onData: ({ data }) => {
        const message = data.data?.chatMessageCreated;
        if (!message || !viewerUserId) return;

        const settings = getChatSoundSettings();

        if (message.systemEventType === "USER_JOINED") {
          if (settings.join) playTone(TONE_FREQUENCIES.join);
          return;
        }
        if (message.mentionUserId === viewerUserId) {
          if (settings.mention) playTone(TONE_FREQUENCIES.mention);
          return;
        }
        if (
          message.replyToMessage?.userId === viewerUserId &&
          message.userId !== viewerUserId
        ) {
          if (settings.reply) playTone(TONE_FREQUENCIES.reply);
        }
      },
    },
  );
}
